"use client";

import { useEffect } from "react";
import { cn } from "utils";

import { Track } from "@/src/lib/tracking";
import { LoadingButton } from "@components/EmailLogin/LoadingButton";
import { InviteCodeInputField } from "./InviteCodeInputField";
import { createInviteCodeCopy } from "./inviteCodeCopy";
import { INVITE_CODE_EVENTS } from "./inviteCodeEvents";
import {
  useInviteCodeApply,
  type InviteCodeApplyOutcome,
} from "./useInviteCodeApply";
import { useKeyboardInsetBottom } from "./useKeyboardInsetBottom";

/**
 * 填码弹层内容 —— H5 底部弹层 / PC 居中弹窗只换壳（`EdgenModal`），内容同一份。
 *
 * Figma：H5 `110013:130019`（空）/ `130031`（无效）/ `130162`（预填）/ `130280`（校验中）；
 * PC `110013:131088` / `131123` / `131158` / `131322`。
 * 判定口径见 {@link useInviteCodeApply}；「Close」/ 遮罩 / 下滑只关闭，不改任何状态（AC-22）。
 */
export interface InviteCodeSheetProps {
  /** 弹层是否打开 —— 每次打开重新预填当前码 */
  open: boolean;
  /** 关闭弹层（Apply 成功 / 移除 / Close） */
  onClose: () => void;
  locale?: string;
}

/** Apply 结论 → 埋点（结果要等 A1 返回才知道，故程序化上报） */
function trackApplyOutcome(outcome: InviteCodeApplyOutcome): void {
  if (outcome.action === "removed") {
    Track.event(INVITE_CODE_EVENTS.remove);
    return;
  }
  if (outcome.action === "applied") {
    Track.event(INVITE_CODE_EVENTS.submit, {
      result: "valid",
      source: outcome.replaced ? "replace" : "new",
    });
    return;
  }
  Track.event(INVITE_CODE_EVENTS.submit, { result: "invalid" });
}

export function InviteCodeSheet({
  open,
  onClose,
  locale,
}: InviteCodeSheetProps) {
  const copy = createInviteCodeCopy(locale);
  const invite = useInviteCodeApply({
    onResult: (outcome) => {
      trackApplyOutcome(outcome);
      if (outcome.action !== "rejected") {
        onClose();
      }
    },
  });
  const keyboardOffset = useKeyboardInsetBottom(open);

  const { reset } = invite;
  useEffect(() => {
    if (open) {
      reset();
    }
    // 只在打开瞬间预填；reset 每次渲染都是新引用，不能放进依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div
      className="flex w-full flex-col items-center gap-4 bg-background_solid_neutral_primary px-5 pb-10 pt-3 md:p-6"
      style={
        keyboardOffset ? { paddingBottom: 40 + keyboardOffset } : undefined
      }
      data-testid="invite-code-sheet"
    >
      {/* Sheet/Grabber（H5） */}
      <div
        className="h-1 w-9 shrink-0 rounded-[2px] bg-border_inverse_transparent10 md:hidden"
        aria-hidden="true"
      />
      <div className="flex w-full flex-col items-start gap-1.5">
        <p className="title_xl text-content_primary">{copy.haveACode}</p>
        <p className="body_m text-content_secondary">{copy.subtitle}</p>
      </div>
      <InviteCodeInputField
        value={invite.value}
        onValueChange={invite.onValueChange}
        error={invite.error}
        locale={locale}
      />
      <LoadingButton
        type="button"
        disabled={!invite.canSubmit}
        loading={invite.checking}
        // Figma 110013:130289 / 131354：`loading-alt` 30px
        loadingIconSize={30}
        onClick={() => void invite.apply()}
        aria-label={copy.applyCode}
        data-openreplay-label="inv_code_sheet_apply"
        data-testid="invite-code-apply"
        className={cn(
          "button_l flex h-12 w-full min-w-20 items-center justify-center rounded-lg px-4 py-3 text-content_inverse_primary",
          invite.checking
            ? "bg-background_brand_overlay70"
            : "bg-background_brand_overlay100",
        )}
      >
        {copy.applyCode}
      </LoadingButton>
      <button
        type="button"
        onClick={onClose}
        data-openreplay-label="inv_code_sheet_close"
        data-testid="invite-code-close"
        className="button_m flex h-11 items-center justify-center rounded-lg px-4 py-3 text-content_primary"
      >
        {copy.close}
      </button>
    </div>
  );
}
